import { z } from 'zod';
import superjson from 'superjson';
import { PermissionLevelArrayValues } from '../../helpers/schema';

export const schema = z.object({
  sessionId: z.string().min(1),
  permissionLevel: z.enum(PermissionLevelArrayValues),
  // userId is for updating a participant's global session permission
  userId: z.number().optional(),

  // fileId/filePath are for file-specific permissions
  fileId: z.string().optional(),
  filePath: z.string().optional(),
  // targetUserId is for when we want to set a file permission specifically for a user
  // We separate this from `userId` to avoid confusion between "Update Participant" and "Update File Permission for User"
  // But the prompt says "userId (for user-specific)". Let's use a distinct field in schema to be safe, or reuse userId if context is clear.
  // To be safe and clear:
  // If userId is present AND (fileId/filePath) is present -> File permission for user.
  // If userId is present AND (fileId/filePath) is MISSING -> Session participant permission.
  // If userId is MISSING AND (fileId/filePath) is present -> File permission for everyone (or default).

  // Let's stick to the prompt's likely intent: reuse `userId` for both contexts.
  // But in the implementation I used `targetUserId` to distinguish. Let's fix the implementation to use `userId` for both, based on context.
  // Wait, if I use `userId` for both, the logic in .ts needs to be:
  // if (fileId || filePath) { ... handle file permission (optionally scoped to userId) ... }
  // else if (userId) { ... handle participant permission ... }

  // So I will rename `targetUserId` to `userId` in the logic to match this schema.
});

export type InputType = z.infer<typeof schema>;

export type OutputType = {
  success: boolean;
};

export const updatePermission = async (
  body: InputType,
  init?: RequestInit,
): Promise<OutputType> => {
  const validatedInput = schema.parse(body);
  const result = await fetch(`/_api/permissions/update`, {
    method: 'POST',
    credentials: 'include',
    body: superjson.stringify(validatedInput),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!result.ok) {
    const errorObject = superjson.parse<{ error: string }>(await result.text());
    throw new Error(errorObject.error);
  }
  return superjson.parse<OutputType>(await result.text());
};
